import { IRequestContext } from '@msdyn365-commerce/core-internal';

import { UCConsentServiceNames } from '../../../integrations/userCentrics/const';
import { UserCentricsService } from '../../../integrations/userCentrics/UserCentricsService';
import { CONSENT_GATED_SERVICES, ConsentGatedCookieNames, CookieNames, DEFAULT_COOKIE_EXPIRATION_DAYS } from './cookie-service.const';

export type CookieName = CookieNames | ConsentGatedCookieNames;

export class CookieService {
    private request: IRequestContext;

    constructor(request: IRequestContext) {
        this.request = request;
    }

    public getCookieExpiration(): Date {
        const cookieDurationFromConfig = Number(this.request.app.config.cookieExpiration);
        const expirationDays = isNaN(cookieDurationFromConfig) ? DEFAULT_COOKIE_EXPIRATION_DAYS : cookieDurationFromConfig;

        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + expirationDays);
        return expiryDate;
    }

    public static getRequestCookie<T = string>(cookieName: CookieName, request: IRequestContext): T | undefined {
        return request.cookies.get<T>(cookieName).value;
    }

    public async setRequestCookie<T>(cookieName: CookieName, cookieValue: T): Promise<void> {
        await this.syncMsdynUserConsentCookie();
        this.request.cookies.set(cookieName, cookieValue, {
            path: '/',
            expires: this.getCookieExpiration()
        });
    }

    public removeRequestCookie(cookieName: CookieName): void {
        this.request.cookies.remove(cookieName);
    }

    public async syncMsdynUserConsentCookie(): Promise<void> {
        if (!UserCentricsService.hasLoaded()) {
            return;
        }

        const hasConsent = await UserCentricsService.hasAnyNonEssentialConsent();
        if (hasConsent) {
            this.request.cookies.setConsentCookie();
        } else {
            // cookies.deleteConsentCookie() does not work on all pages, so we must delete the cookie by name
            this.removeRequestCookie(CookieNames.UserConsent);
        }
    }

    public async syncConsentGatedCookies(): Promise<void> {
        if (!UserCentricsService.hasLoaded()) {
            return;
        }

        const controllerId = await UserCentricsService.getControllerId();
        if (!controllerId) {
            return;
        }

        CONSENT_GATED_SERVICES.forEach(service => {
            this.setConsentGatedCookie(service.serviceName, service.cookieName, controllerId);
        });
    }

    public async getConsentedControllerId(serviceCookie: ConsentGatedCookieNames): Promise<string | undefined> {
        if (typeof window === 'undefined') {
            return CookieService.getRequestCookie(serviceCookie, this.request);
        }

        const service = CONSENT_GATED_SERVICES.find(gatedService => gatedService.cookieName === serviceCookie);
        const hasConsent = service && (await UserCentricsService.hasServiceConsent(service.serviceName));
        if (!hasConsent) {
            return undefined;
        }

        return UserCentricsService.getControllerId();
    }

    public async setConsentGatedCookie(
        serviceName: UCConsentServiceNames,
        cookieName: ConsentGatedCookieNames,
        controllerId: string
    ): Promise<void> {
        const hasConsent = await UserCentricsService.hasServiceConsent(serviceName);
        if (!hasConsent) {
            this.removeConsentGatedCookie(cookieName);
            return;
        }

        await this.setRequestCookie(cookieName, controllerId);
    }

    public removeConsentGatedCookie(cookieName: ConsentGatedCookieNames): void {
        this.removeRequestCookie(cookieName);
    }
}
